/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { ButtonIcon, Icon, Popover, Tooltip, Typography } from "./..";
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import React, { forwardRef, isValidElement, memo, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { bgColor, borderNone, cursorPointer, displayFlex, flexCol, flexRow, gap, itemsCenter, outlineNone, parseHeight, pd, pdl } from "../../styles/general";
import { classNames } from "../../utils";
const ButtonMore = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  anchor,
  buttonProps,
  className,
  disabled,
  id,
  labelProp,
  options,
  optionType,
  popoverProp,
  style
}, reference) => {
  if (!reference) reference = useRef(null);
  const isOptionFull = optionType === 'full';
  const ref = useRef(null);
  const [openState, setOpenState] = useState(false);
  const [iconState, setIconState] = useState(false);
  const _PopoverCSS = PopoverCSS(isOptionFull);
  const openPopover = () => {
    setOpenState(true);
  };
  const closePopover = () => {
    setOpenState(false);
  };
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const renderOptions = () => {
    return options.map((item, idx) => {
      if (item !== null && item !== void 0 && item.hidden) return;else if ( /*#__PURE__*/isValidElement(item)) return item;else {
        const icon = item === null || item === void 0 ? void 0 : item.icon;
        const label = item === null || item === void 0 ? void 0 : item.label;
        const action = () => {
          closePopover();
          if (typeof (item === null || item === void 0 ? void 0 : item.action) === 'function') item === null || item === void 0 ? void 0 : item.action();
        };
        if (icon && !iconState) setIconState(true);
        return isOptionFull ? jsx("button", {
          key: idx,
          css: OptionFullCSS,
          className: 'DGN-UI-ButtonMore-Option',
          onClick: action
        }, icon ? jsx(Icon, {
          className: 'DGN-UI-ButtonMore-Option-Icon',
          name: icon,
          width: 20,
          height: 20,
          color: 'system/dark'
        }) : null, jsx(Typography, {
          css: !icon && iconState && ExtraPaddingCSS,
          className: 'DGN-UI-ButtonMore-Label',
          ...labelProp
        }, label)) : jsx(Tooltip, {
          key: idx,
          className: 'DGN-UI-ButtonMore-Option',
          title: label
        }, jsx(ButtonIcon, {
          circular: true,
          name: icon,
          onClick: action,
          viewType: 'text'
        }));
      }
    });
  };
  const renderAnchor = () => {
    var _anchor, _anchor2, _anchor2$props;
    if (!((_anchor = anchor) !== null && _anchor !== void 0 && _anchor.type)) return null;
    const AnchorTag = /*#__PURE__*/React.cloneElement(anchor, {
      ref: ref => {
        anchor = ref;
      },
      onClick: openPopover,
      id: id,
      style: {
        ...style,
        ...(((_anchor2 = anchor) === null || _anchor2 === void 0 ? void 0 : (_anchor2$props = _anchor2.props) === null || _anchor2$props === void 0 ? void 0 : _anchor2$props.style) || {})
      },
      className: classNames('DGN-UI-ButtonMore', className)
    });
    return AnchorTag;
  };
  return useMemo(() => {
    return jsx(Popover, {
      css: _PopoverCSS,
      open: openState,
      onClose: closePopover,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left'
      },
      anchor: anchor ? renderAnchor() : jsx(ButtonIcon, {
        ref: ref,
        circular: true,
        name: 'more',
        viewType: 'text',
        id: id,
        style: style,
        className: classNames('DGN-UI-ButtonMore', className),
        disabled: disabled,
        onClick: openPopover,
        ...buttonProps
      }),
      ...popoverProp
    }, renderOptions());
  }, [buttonProps, className, disabled, id, options, optionType, popoverProp, style, iconState, openState]);
}));
const PopoverCSS = isOptionFull => css`
    ${displayFlex};
    ${isOptionFull ? flexCol : flexRow};
    ${pd(isOptionFull ? [1, 0] : [0, 2])}
`;
const OptionFullCSS = css`
    ${displayFlex};
    ${flexRow};
    ${itemsCenter};
    ${cursorPointer};
    ${outlineNone};
    ${borderNone}
    ${parseHeight(28)};
    ${pd([0, 4])};
    ${gap([0, 1])};
    ${bgColor('system/white')};
    transition: all 0.5s ease;
    &:hover,
    &:focus {
        ${bgColor('fill/hover')};
    }
    &:active {
        ${bgColor('fill/pressed')};
    }
`;
const ExtraPaddingCSS = css`
    ${pdl([6])};
`;
ButtonMore.defaultProps = {
  buttonProps: {},
  className: '',
  disabled: false,
  options: [],
  optionType: 'full',
  style: {}
};
ButtonMore.propTypes = {
  /** [Props](https://core.diginet.com.vn/ui/?path=/docs/components-buttonicon) applied to the main `ButtonIcon`. */
  buttonProps: PropTypes.object,
  /** Class for component. */
  className: PropTypes.string,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** [Props](https://core.diginet.com.vn/ui/?path=/docs/components-typography) applied to the `Typography` label. */
  labelProp: PropTypes.object,
  /** Option(s) in popover. */
  option: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    icon: PropTypes.string,
    action: PropTypes.func,
    hidden: PropTypes.bool
  })),
  /** The variant of option's type. */
  optionType: PropTypes.oneOf(['full', 'icon']),
  /** [Props](https://core.diginet.com.vn/ui/?path=/docs/components-popover) applied to the main `Popover`. */
  popoverProp: PropTypes.object,
  /** Style inline of component. */
  style: PropTypes.object
};
export { ButtonMore };
export default OptionWrapper(ButtonMore);